/**
 * Role Management Service
 * Part of TASK-334: Frontend Admin Panel
 *
 * Handles role CRUD operations and permission management
 * for the admin panel.
 */

import { httpClient } from '../../lib/httpClient';
import type {
  Role,
  RoleListResponse,
  RoleCreateData,
  RoleUpdateData,
  RoleDuplicateData,
  PermissionDefinitions,
  PermissionGroup,
} from '../../types/admin';

class RoleService {
  private readonly basePath = '/api/admin/roles';

  /**
   * Get all available permission definitions
   */
  async getPermissionDefinitions(): Promise<PermissionDefinitions> {
    try {
      const data = await httpClient.get<PermissionDefinitions>(`${this.basePath}/permissions`);
      return data;
    } catch (error) {
      console.error('Error fetching permission definitions:', error);
      throw error;
    }
  }

  /**
   * List all roles (system + custom)
   */
  async listRoles(): Promise<RoleListResponse> {
    try {
      const data = await httpClient.get<RoleListResponse>(this.basePath);
      return data;
    } catch (error) {
      console.error('Error listing roles:', error);
      throw error;
    }
  }

  /**
   * Get a single role by ID
   */
  async getRole(roleId: string): Promise<Role> {
    try {
      const data = await httpClient.get<Role>(`${this.basePath}/${roleId}`);
      return data;
    } catch (error) {
      console.error('Error fetching role:', error);
      throw error;
    }
  }

  /**
   * Create a new custom role
   */
  async createRole(roleData: RoleCreateData): Promise<Role> {
    try {
      const data = await httpClient.post<Role>(this.basePath, roleData);
      return data;
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }

  /**
   * Update a role's permissions
   */
  async updateRole(roleId: string, updates: RoleUpdateData): Promise<Role> {
    try {
      const data = await httpClient.put<Role>(`${this.basePath}/${roleId}`, updates);
      return data;
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  /**
   * Delete a custom role
   */
  async deleteRole(roleId: string): Promise<{ message: string }> {
    try {
      const data = await httpClient.delete<{ message: string }>(`${this.basePath}/${roleId}`);
      return data;
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  }

  /**
   * Duplicate an existing role
   */
  async duplicateRole(roleId: string, duplicateData: RoleDuplicateData): Promise<Role> {
    try {
      const data = await httpClient.post<Role>(
        `${this.basePath}/${roleId}/duplicate`,
        duplicateData
      );
      return data;
    } catch (error) {
      console.error('Error duplicating role:', error);
      throw error;
    }
  }

  // ==================== Permission Matrix Helpers ====================

  /**
   * Transform permission definitions into grouped format for UI
   */
  transformToPermissionGroups(
    definitions: PermissionDefinitions,
    enabledPermissions: string[]
  ): PermissionGroup[] {
    const enabledSet = new Set(enabledPermissions);

    return definitions.resources.map((resource) => ({
      resource: resource.resource,
      description: resource.description,
      permissions: resource.actions.map((action) => {
        const permission = `${resource.resource}:${action.action}`;
        return {
          action: action.action,
          description: action.description,
          permission,
          enabled: enabledSet.has(permission),
        };
      }),
    }));
  }

  /**
   * Extract enabled permissions from permission groups
   */
  extractEnabledPermissions(groups: PermissionGroup[]): string[] {
    const permissions: string[] = [];

    groups.forEach((group) => {
      group.permissions.forEach((perm) => {
        if (perm.enabled) {
          permissions.push(perm.permission);
        }
      });
    });

    return permissions;
  }

  /**
   * Check if a role has a specific permission
   */
  hasPermission(role: Role, permission: string): boolean {
    return role.permissions.includes(permission) || role.permissions.includes('*');
  }

  /**
   * Check if a role has all permissions for a resource
   */
  hasAllResourcePermissions(role: Role, resource: string): boolean {
    if (role.permissions.includes('*') || role.permissions.includes(`${resource}:*`)) {
      return true;
    }
    return false;
  }

  /**
   * Get permission count by category
   */
  getPermissionCountByCategory(permissions: string[]): Record<string, number> {
    const counts: Record<string, number> = {};

    permissions.forEach((perm) => {
      const [resource] = perm.split(':');
      counts[resource] = (counts[resource] || 0) + 1;
    });

    return counts;
  }
}

export const roleService = new RoleService();
